// SiegerehrungDisplayPage.jsx — Vollbild fuer zweiten Bildschirm / Beamer.
// Gesteuert vom Hauptfenster via localStorage + BroadcastChannel.
//
// Gestaltung: styles/leinwand.css, dieselbe wie die uebrigen Leinwaende.
// Kopfband, Fussleiste und Typografie kommen von dort; das Podest ist das
// Eigene dieser Anzeige.
import React, { useState, useEffect } from 'react';
import '../styles/leinwand.css';
import './SiegerehrungDisplayPage.css';
// Platzabzeichen statt 🥇🥈🥉 — dieselben wie auf der Formen-Leinwand.
import Platz from '../components/leinwand/Platz';

const CHANNEL = 'tda-siegerehrung';

export default function SiegerehrungDisplayPage() {
  const [data, setData]           = useState(null);
  const [animKey, setAnimKey]     = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Overflow hidden
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  // Auto-Vollbild beim Laden (klappt wenn via Popup aus User-Geste geöffnet)
  useEffect(() => {
    const timer = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fullscreen-Status tracken
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }
  };

  // Chat-Widget und externe Elemente verstecken
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT','STYLE','LINK','META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const LS_KEY = 'tda-siegerehrung-state';

    const applyState = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed?.type === 'show') {
          setData({ kategorie: parsed.kategorie, idx: parsed.idx, total: parsed.total });
          setAnimKey(k => k + 1);
        } else {
          setData(null);
        }
      } catch (e) {}
    };

    // 1) Sofort lesen beim Öffnen
    applyState(localStorage.getItem(LS_KEY));

    // 2) storage-Event: feuert in ALLEN anderen Fenstern wenn localStorage sich ändert
    //    (zuverlässiger als BroadcastChannel in Safari)
    const onStorage = (e) => {
      if (e.key === LS_KEY) applyState(e.newValue);
    };
    window.addEventListener('storage', onStorage);

    // 3) BroadcastChannel als zusätzlicher Kanal (für gleichen Tab)
    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => applyState(e.data);
    } catch (e) {}

    return () => {
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, []);

  // Setup-Overlay: sichtbar solange kein Vollbild aktiv
  if (!isFullscreen) {
    return <SetupOverlay onFullscreen={toggleFullscreen} />;
  }

  return !data
    ? <StandbyScreen onFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />
    : <PodiumDisplay key={animKey} data={data} onFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />;
}

// ── Setup Overlay ─────────────────────────────────────────────────────
function SetupOverlay({ onFullscreen }) {
  const [copied, setCopied] = useState(false);
  const displayUrl = `${window.location.origin}/siegerehrung-display`;

  const copyUrl = () => {
    navigator.clipboard.writeText(displayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="sdp-setup">
      <div className="sdp-setup-kanji">表彰</div>
      <div className="sdp-setup-card">
        <div className="sdp-setup-title">Zweiter Bildschirm einrichten</div>

        {/* Option A: Fenster verschieben */}
        <div className="sdp-setup-option">
          <div className="sdp-setup-option-label">Option A — Fenster verschieben</div>
          <div className="sdp-setup-drag">
            <div className="sdp-drag-arrow">←</div>
            <div className="sdp-drag-text">
              Fenster an den <strong>Bildschirmrand ziehen</strong> — macOS schiebt es auf den anderen Monitor
            </div>
          </div>
          <button className="sdp-setup-btn" onClick={onFullscreen}>
            ⛶ Vollbild starten
          </button>
        </div>

        <div className="sdp-setup-divider">oder</div>

        {/* Option B: URL direkt öffnen */}
        <div className="sdp-setup-option">
          <div className="sdp-setup-option-label">Option B — URL direkt öffnen</div>
          <p className="sdp-setup-hint">Im Browser des externen Monitors diese URL eingeben:</p>
          <div className="sdp-setup-url">{displayUrl}</div>
          <button className="sdp-setup-copy" onClick={copyUrl}>
            {copied ? '✓ Kopiert!' : '📋 URL kopieren'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Standby ───────────────────────────────────────────────────────────
function StandbyScreen({ onFullscreen, isFullscreen }) {
  return (
    <div className="lw sdp" onClick={onFullscreen} title="Klicken für Vollbild">
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">Tiger &amp; Dragon Association</div>
        </div>
        <div className="lw-kopf-titel">表彰 SIEGEREHRUNG</div>
        <div className="lw-kopf-status wartet">BEREIT</div>
      </div>

      <div className="lw-buehne">
        <div className="lw-kanji wasserzeichen" aria-hidden>武</div>
        <div className="lw-name">TDA</div>
        <div className="lw-bereit-text">BEREIT</div>
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Tiger &amp; Dragon Association International</div>
        <div className="lw-fuss-wert">
          {isFullscreen ? '' : 'Klicken startet das Vollbild'}
        </div>
      </div>
    </div>
  );
}

// ── Podium Display ────────────────────────────────────────────────────
function PodiumDisplay({ data, onFullscreen, isFullscreen }) {
  const { kategorie, idx, total } = data;
  const platz1 = kategorie.plaetze.find(p => p.platzierung === 1);
  const platz2 = kategorie.plaetze.find(p => p.platzierung === 2);
  const platz3 = kategorie.plaetze.find(p => p.platzierung === 3);
  const weiteres = kategorie.plaetze
    .filter(p => p.platzierung > 3)
    .sort((a, b) => a.platzierung - b.platzierung);

  // Eine Saeule je Platz: die Person steht auf ihrem Sockel, alle Sockel enden
  // auf derselben Linie. Frueher lagen Personen und Sockel in zwei getrennten
  // Reihen, und die Staffelung stand auf dem Kopf -- der Dritte oben, der
  // Sieger unten.
  const renderSaeule = (platz, medalLabel, rang, delay) => (
    <div className={`lw-podest-saeule lw-podest-saeule--${rang}${platz ? '' : ' leer'}`} style={{ '--delay': delay }}>
      <div className="lw-podest-person">
        <div className="sdp-medal-wrap">
          <Platz platz={rang} gross />
        </div>
        {platz ? (
          <>
            <div className="lw-podest-name">
              <span className="lw-podest-vorname">{platz.vorname}</span>
              <span className="lw-podest-nachname">{platz.nachname}</span>
            </div>
            <div className="lw-podest-verein">{platz.verein_name}</div>
            {platz.punkte != null && (
              <div className="lw-podest-punkte">{parseFloat(platz.punkte).toFixed(2)} Pkt</div>
            )}
          </>
        ) : (
          <div className="sdp-empty-slot">{medalLabel}</div>
        )}
      </div>
      <div className="lw-podest-sockel"><span>{rang}</span></div>
    </div>
  );

  const untertitel = [kategorie.division_code, kategorie.division_type].filter(Boolean).join(' · ');

  return (
    <div className="lw sdp" onClick={onFullscreen}>
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">{kategorie.kategorie_name}</div>
          {untertitel && <div className="lw-kopf-kat">{untertitel}</div>}
        </div>
        <div className="lw-kopf-titel">表彰 SIEGEREHRUNG</div>
        <div className="lw-kopf-status">{idx + 1} / {total}</div>
      </div>

      <div className="lw-buehne">
        <div className="lw-kanji wasserzeichen" aria-hidden>武</div>

        <div className="lw-podest">
          {renderSaeule(platz2, '2. Platz', 2, '0.2s')}
          {renderSaeule(platz1, '1. Platz', 1, '0.05s')}
          {renderSaeule(platz3, '3. Platz', 3, '0.35s')}
        </div>

        {weiteres.length > 0 && (
          <div className="sdp-weiteres">
            <div className="sdp-w-label">Weitere Platzierungen</div>
            <div className="sdp-w-list">
              {weiteres.map((p, i) => (
                <div key={i} className="sdp-w-entry">
                  <span className="sdp-w-platz">{p.platzierung}.</span>
                  <span className="sdp-w-name">{p.vorname} {p.nachname}</span>
                  <span className="sdp-w-club">{p.verein_name}</span>
                  {p.punkte != null && (
                    <span className="sdp-w-pts">{parseFloat(p.punkte).toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Tiger &amp; Dragon Association</div>
        <div className="lw-fuss-wert">
          {isFullscreen ? 'Klicken beendet das Vollbild' : 'Klicken für Vollbild'}
        </div>
      </div>
    </div>
  );
}
